/**
 * The audit button, and the trail it opens.
 *
 * Reachable from every list row and every record, because a change nobody can
 * find is a change nobody can question. The rows are written by database
 * triggers rather than by application code, which is why this front end has no
 * part in producing them and no way to suppress one.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, ApiError } from '../api/client'
import { useSession } from '../app/session'
import { Drawer } from './Drawer'

const Ctx = createContext<{ openAudit: (link: string) => void }>({ openAudit: () => {} })

export function useAudit() { return useContext(Ctx).openAudit }

export function AuditProvider({ children }: { children: ReactNode }) {
  const [link, setLink] = useState<string | null>(null)
  const openAudit = useCallback((target: string) => setLink(target), [])
  const value = useMemo(() => ({ openAudit }), [openAudit])

  return (
    <Ctx.Provider value={value}>
      {children}
      <Drawer title="Audit trail" open={Boolean(link)} onClose={() => setLink(null)}>
        {link && <AuditBody link={link} />}
      </Drawer>
    </Ctx.Provider>
  )
}

function AuditBody({ link }: { link: string }) {
  const { lbl } = useSession()
  const { data, error, isLoading } = useQuery({
    queryKey: ['audit', link],
    queryFn: () => api.audit(link),
    staleTime: 15_000,
  })

  if (isLoading) return <p className="muted">Loading…</p>
  if (error) {
    const problem = error as ApiError
    return <p className="ex-missing">{problem.detail || 'That trail could not be read.'}</p>
  }
  if (!data) return null

  const { trail, note } = data
  const stamp = (iso: string) => {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const pad = (n: number) => String(n).padStart(2, '0')
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()} ` +
           `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  return (
    <div className="ex-body">
      <div className="ex-meta" style={{ margin: '0 0 14px', padding: 0, border: 0 }}>
        <span className="b muted">{trail.schema}.{trail.table}</span>
        <span className="b muted mono">{trail.entity_key.slice(0, 8)}</span>
        <span className="b info">
          {trail.entries.length} change{trail.entries.length === 1 ? '' : 's'}
        </span>
      </div>

      {note && (
        <div className="cav" style={{ marginBottom: 14 }}>
          <b>This table is not audited row by row.</b> {note.reason}
        </div>
      )}

      {trail.entries.length === 0 ? (
        <p className="muted">
          No change has been recorded against this record yet. The trigger is
          attached, so the absence means nothing has changed since the row was
          written, not that changes went unrecorded.
        </p>
      ) : (
        <ul className="trail">
          {trail.entries.map((e) => (
            <li key={e.change_id} className={`op-${e.operation}`}>
              <div className="when">{stamp(e.changed_at)}</div>
              <div className="who">
                {e.actor_label}
                <span className={`b ${e.operation === 'delete' ? 'bad'
                  : e.operation === 'insert' ? 'good' : 'info'}`}>{e.operation}</span>
                {e.acting_role && <span className="b muted">{e.acting_role}</span>}
                {e.partner_code && <span className="b muted">{e.partner_code}</span>}
                <span className="b muted">{lbl(e.source)}</span>
              </div>
              {e.reason && (
                <div className="reason"><b>Stated reason.</b> {e.reason}</div>
              )}
              {e.diffs.length > 0 && (
                <table className="diff">
                  <tbody>
                    {e.diffs.map((d) => (
                      <tr key={d.column}>
                        <th>{d.column}</th>
                        <td>
                          {d.old !== null && <span className="old">{d.old}</span>}
                          {d.old !== null && d.new !== null && ' → '}
                          {d.new !== null && <span className="new">{d.new}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {e.diff_total > e.diffs.length && (
                <div className="xs muted">
                  {e.diff_total - e.diffs.length} further
                  field{e.diff_total - e.diffs.length === 1 ? '' : 's'} not shown.
                </div>
              )}
              {e.client_ip && <div className="xs muted">from {e.client_ip}</div>}
            </li>
          ))}
        </ul>
      )}

      <div className="cav" style={{ marginTop: 16 }}>
        <b>How this trail is produced.</b> Rows are written by database triggers
        rather than by application code. Application-written audit has one
        failure mode that cannot be engineered away, the code path that forgot,
        and triggers have no such path. Credential material and encrypted
        identifiers are redacted inside the trigger before the row is written,
        so the trail records that they changed and never their value.
      </div>
    </div>
  )
}

export function AuditButton({ link, children }: { link: string; children?: ReactNode }) {
  const openAudit = useAudit()
  return (
    <button className="abtn" type="button"
            onClick={(e) => { e.stopPropagation(); openAudit(link) }}
            title="Audit trail for this record">{children || 'Audit'}</button>
  )
}
